/**
 * Authentication service for handling user authentication
 */

const API_ENDPOINTS = {
    LOGIN: 'https://kosarev.app/api/sso/v1/login',
    REGISTER: 'https://kosarev.app/api/sso/v1/register',
    VERIFY: 'https://kosarev.app/api/sso/v1/verify'
};

const STORAGE_KEYS = {
    AUTH_TOKEN: 'authToken',
    USER_ID: 'userId'
};

/**
 * Response object structure returned by auth methods
 * @typedef {Object} AuthResponse
 * @property {boolean} success - Whether the operation succeeded
 * @property {string} message - Human-readable message about the result
 * @property {Object} [data] - The response data if successful
 * @property {Error} [error] - The error object if failed
 */

/**
 * Authenticate user with email and password
 * @param {string} email - User email
 * @param {string} password - User password
 * @returns {Promise<AuthResponse>} Authentication result
 */
export async function login(email, password) {
    try {
        if (!email || !password) {
            return {
                success: false,
                message: 'Email and password are required',
                error: new Error('Email and password are required')
            };
        }

        const response = await fetch(API_ENDPOINTS.LOGIN, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ email, password }),
            credentials: 'include'
        });

        let data;
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
            data = await response.json();
        } else {
            const text = await response.text();
            data = { message: text };
        }

        if (!response.ok) {
            const errorMessage = data.message || `Authentication failed: ${response.statusText}`;
            return {
                success: false,
                message: errorMessage,
                error: new Error(errorMessage),
                status: response.status
            };
        }

        if (data.token) {
            localStorage.setItem(STORAGE_KEYS.AUTH_TOKEN, data.token);

            if (data.user_id) {
                localStorage.setItem(STORAGE_KEYS.USER_ID, data.user_id.toString());
            }

            window.dispatchEvent(new Event('storage'));
        }

        return {
            success: true,
            message: 'Authentication successful',
            data
        };
    } catch (error) {
        console.error('Authentication error:', error);

        return {
            success: false,
            message: error.message || 'An unexpected error occurred during authentication',
            error
        };
    }
}

/**
 * Register a new user
 * @param {string} email - User email
 * @param {string} password - User password
 * @returns {Promise<AuthResponse>} Registration result
 */
export async function register(email, password) {
    try {
        if (!email || !password) {
            return {
                success: false,
                message: 'Email and password are required',
                error: new Error('Email and password are required')
            };
        }

        if (password.length < 6) {
            return {
                success: false,
                message: 'Password must be at least 6 characters long',
                error: new Error('Password too short')
            };
        }

        const response = await fetch(API_ENDPOINTS.REGISTER, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ email, password }),
            credentials: 'include'
        });

        let data;
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
            data = await response.json();
        } else {
            const text = await response.text();
            data = { message: text };
        }

        if (!response.ok) {
            const errorMessage = data.message || `Registration failed: ${response.statusText}`;
            return {
                success: false,
                message: errorMessage,
                error: new Error(errorMessage),
                status: response.status
            };
        }

        return {
            success: true,
            message: 'Registration successful',
            data
        };
    } catch (error) {
        console.error('Registration error:', error);

        return {
            success: false,
            message: error.message || 'An unexpected error occurred during registration',
            error
        };
    }
}

/**
 * Verify the current authentication token
 * @returns {Promise<AuthResponse>} Verification result
 */
export async function verifyToken() {
    try {
        const token = localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);

        if (!token) {
            return {
                success: false,
                message: 'No authentication token found',
                error: new Error('No token')
            };
        }

        const response = await fetch(API_ENDPOINTS.VERIFY, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${token}`,
            },
            credentials: 'include'
        });

        let data;
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
            data = await response.json();
        } else {
            const text = await response.text();
            data = { message: text };
        }

        if (!response.ok) {
            logout();

            const errorMessage = data.message || `Token verification failed: ${response.statusText}`;
            return {
                success: false,
                message: errorMessage,
                error: new Error(errorMessage),
                status: response.status
            };
        }

        if (data.user_id) {
            localStorage.setItem(STORAGE_KEYS.USER_ID, data.user_id.toString());
        }

        return {
            success: true,
            message: 'Token verified',
            data
        };
    } catch (error) {
        console.error('Token verification error:', error);

        return {
            success: false,
            message: error.message || 'An unexpected error occurred during token verification',
            error
        };
    }
}

/**
 * Check if user is currently authenticated
 * @returns {boolean} True if user has a valid auth token
 */
export function isAuthenticated() {
    return !!localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
}

/**
 * Get the current authentication token
 * @returns {string|null} The authentication token or null if not authenticated
 */
export function getAuthToken() {
    return localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
}

/**
 * Get the current user ID
 * @returns {string|null} The user ID or null if not available
 */
export function getUserId() {
    return localStorage.getItem(STORAGE_KEYS.USER_ID);
}

/**
 * Logout the current user
 */
export function logout() {
    localStorage.removeItem(STORAGE_KEYS.AUTH_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.USER_ID);

    window.dispatchEvent(new Event('storage'));
}